import { useEffect } from 'react'
import { Button, Checkbox, FilterBar, Group, Segmented, Select, Text, TextInput } from '@grovus/design-system'
import { OVERLAY_LAYER } from '../components/overlayRegistry'
import { useDisclosurePanel } from '../components/useDisclosurePanel'
import { AccountToggles, type AccountToggleProps } from './AccountToggles'
import { CHOOSER_PANEL, ColumnChooser } from './ColumnChooser'
import type { ColumnKey } from './columns'
import { VERDICT_KEYS, type CreativeFormat, type Dimension, type VerdictKey } from './contract'
import { fill } from './format'
import type { Row, SortKey } from './partition'
import { UNASSIGNED } from './partition'
import { labelOf, verdictLabel, type ViewContext } from './view'

export type ViewMode = 'table' | 'grid'

export interface FilterState {
  hypothesisId: string | null
  techniqueId: string | null
  messageId: string | null
  offerId: string | null
  /** The Format axis (GRO-174): image, video or carousel. */
  format: CreativeFormat | null
  audienceId: string | null
  minSpend: number
  minConversions: number
  showSamples: boolean
  /** A display filter, like the floors: the tiles do not follow it (GRO-64, D4). */
  verdict: VerdictKey | null
  /** The name search, a display filter after the verdict (GRO-165). `''` is off. */
  name: string
}

interface Props {
  ctx: ViewContext
  /** Every creative in the response, before the client filters: the options come from here. */
  all: Row[]
  filters: FilterState
  onChange: (next: Partial<FilterState>) => void
  view: ViewMode
  onView: (view: ViewMode) => void
  sort: SortKey
  dir: 'asc' | 'desc'
  onSort: (sort: SortKey) => void
  resultCount: string
  hidden: ReadonlySet<ColumnKey>
  onToggleColumn: (key: ColumnKey) => void
  onShowAllColumns: () => void
  /** The shell's account selection. Absent where the screen renders without a shell. */
  accountToggles?: AccountToggleProps
  onExport: () => void
  onExportXlsx: () => void
  /** Rows on screen that answer the scope selected now; false while a load is pending. */
  canExport: boolean
}

/** Scale first and Kill last, the order the two verdict Questions read. */

const ALL = '__all__'

/**
 * Options for a value axis, built from the creatives actually in the response.
 *
 * Not from `dimensions`: that list covers the whole account scope, so offering all of it
 * here would put values in the dropdown with nothing behind them once an audience is
 * scoped. The audience filter is the exception and reads `dimensions` directly, because
 * narrowing it by what is currently in scope would shrink the list to the one already
 * chosen.
 *
 * `unassignedLabel` adds the none-assigned option when a creative in the response carries
 * no value on this axis - the same read-from-the-creatives rule, so the select never
 * offers a value with nothing behind it, and the value is the sentinel `segment`
 * interprets.
 */
function optionsFrom(
  rows: Row[],
  pick: (row: Row) => string | null,
  values: Dimension[],
  ctx: ViewContext,
  unassignedLabel?: string,
): Array<{ value: string; label: string }> {
  const ids = [...new Set(rows.map(pick))]
  const options = ids
    .filter((id): id is string => id !== null)
    .map((id) => ({ value: id, label: labelOf(values, id, ctx.lang) ?? id }))
    .sort((left, right) => left.label.localeCompare(right.label))
  if (unassignedLabel !== undefined && rows.some((row) => pick(row) === null)) {
    options.push({ value: UNASSIGNED, label: unassignedLabel })
  }
  return options
}

export function CreativesFilterBar({
  ctx, all, filters, onChange, view, onView, sort, dir, onSort, resultCount,
  hidden, onToggleColumn, onShowAllColumns, accountToggles, onExport, onExportXlsx, canExport,
}: Props) {
  const { t, lang } = ctx
  // One instance for both panels, so opening one closes the other, on the page layer so
  // that opening a creative closes whichever is open (GRO-64, D8).
  const panels = useDisclosurePanel('creatives-panel', OVERLAY_LAYER.pageDisclosure)
  // The chooser exists in table view only. Leaving it by keyboard or through a saved view
  // presses nothing outside the panel, so without this the hook would keep it open and
  // registered over nothing, and it would come back open, unfocused, on the return.
  const chooserOpen = panels.isOpen(CHOOSER_PANEL)
  const { dismiss } = panels
  useEffect(() => {
    if (view !== 'table' && chooserOpen) dismiss()
  }, [view, chooserOpen, dismiss])

  const sortOptions: Array<[SortKey, string]> = [
    ['spend', t.sortSpend], ['share', t.sortShare], ['purch', t.sortPurch], ['roas', t.sortRoas],
    ['adj', t.sortAdj], ['cpa', t.sortCpa], ['aov', t.sortAov], ['cpc', t.sortCpc], ['cpm', t.sortCpm],
    ['activeDays', t.sortActiveDays], ['ctr', t.sortCtr],
    ['hook', t.sortHook], ['hold', t.sortHold], ['ads', t.sortAds], ['accounts', t.sortAccounts],
    ['hypothesis', t.sortHypothesis], ['technique', t.sortTechnique], ['name', t.sortName],
    ['format', t.format],
    ['verdict', t.sortVerdict],
  ]

  const select = (
    key: 'hypothesisId' | 'techniqueId' | 'messageId' | 'offerId' | 'audienceId',
    label: string,
    options: Array<{ value: string; label: string }>,
  ) => (
    <Select
      key={key}
      label={label}
      data={[{ value: ALL, label: t.allOpt }, ...options]}
      value={filters[key] ?? ALL}
      onChange={(value) => onChange({ [key]: value === ALL || value === null ? null : value })}
    />
  )

  return (
    <FilterBar
      trailing={
        // Wraps since GRO-64: the export button and the gear joined the count and the view
        // switch, and at 390 in Russian the four on one line ran 22px past the viewport.
        <Group gap="md" wrap="wrap">
          <Text size="xs" data-testid="creatives-count">{resultCount}</Text>
          {/* The workbook first: its figures are numbers in any spreadsheet locale, where
              the CSV's decimals read as text under a decimal comma (GRO-81). */}
          <Button
            variant="default"
            size="compact-sm"
            onClick={onExportXlsx}
            disabled={!canExport}
            title={canExport ? undefined : t.exportPending}
            className="creatives-export"
            data-testid="creatives-export-xlsx"
          >
            {t.exportXlsx}
          </Button>
          <Button
            variant="default"
            size="compact-sm"
            onClick={onExport}
            disabled={!canExport}
            title={canExport ? undefined : t.exportPending}
            aria-label={t.exportCsv}
            className="creatives-export"
            data-testid="creatives-export"
          >
            {t.exportCsvShort}
          </Button>
          {/* The table has columns to choose; the grid has none. The hidden set lives on
              the screen, so it survives the switch. */}
          {view === 'table' && (
            <ColumnChooser
              ctx={ctx}
              hidden={hidden}
              onToggle={onToggleColumn}
              onShowAll={onShowAllColumns}
              sort={sort}
              dir={dir}
              panels={panels}
            />
          )}
          <Segmented
            aria-label={t.table}
            value={view}
            onChange={(value) => onView(value as ViewMode)}
            data={[{ value: 'table', label: t.table }, { value: 'grid', label: t.grid }]}
          />
        </Group>
      }
    >
      <div data-testid="creatives-filters" style={{ display: 'contents' }}>
        {accountToggles && <AccountToggles t={t} {...accountToggles} panels={panels} />}
        <TextInput
          type="search"
          label={t.searchName}
          value={filters.name}
          onChange={(event) => onChange({ name: event.currentTarget.value })}
          data-testid="creatives-search"
        />
        {select(
          'hypothesisId',
          t.hypothesis,
          optionsFrom(all, (row) => row.hypothesisId, ctx.dimensions.hypotheses, ctx, t.hypUnassigned),
        )}
        <Select
          label={t.format}
          data={[
            { value: ALL, label: t.allOpt },
            { value: 'image', label: t.image },
            { value: 'video', label: t.video },
            { value: 'carousel', label: t.carousel },
          ]}
          value={filters.format ?? ALL}
          onChange={(value) =>
            onChange({ format: value === ALL || value === null ? null : (value as CreativeFormat) })
          }
        />
        {select(
          'techniqueId',
          t.technique,
          optionsFrom(all, (row) => row.techniqueId, ctx.dimensions.techniques, ctx, t.techUnassigned),
        )}
        {select(
          'messageId',
          t.message,
          optionsFrom(all, (row) => row.messageId, ctx.dimensions.messages, ctx, t.msgUnassigned),
        )}
        {select(
          'offerId',
          t.offer,
          optionsFrom(all, (row) => row.offerId, ctx.dimensions.offers, ctx, t.offerUnassigned),
        )}
        {select(
          'audienceId',
          t.audience,
          ctx.dimensions.audiences.map((audience) => ({
            value: audience.id,
            label: audience.label[lang],
          })),
        )}

        <Select
          label={t.verdictCol}
          data={[
            { value: ALL, label: t.allOpt },
            ...VERDICT_KEYS.map((key) => ({ value: key, label: verdictLabel(key, t) })),
          ]}
          value={filters.verdict ?? ALL}
          onChange={(value) =>
            onChange({ verdict: value === ALL || value === null ? null : (value as VerdictKey) })
          }
          data-testid="verdict-filter"
        />

        {/* The reading floor. Two display filters, both off at zero, both captioned with
            the window they read against so "minimum 50" is never mistaken for a lifetime
            figure. */}
        <TextInput
          label={`${fill(t.minPeriod, { d: ctx.periodDays })} · ${t.spend}`}
          type="number"
          min={0}
          value={filters.minSpend === 0 ? '' : String(filters.minSpend)}
          onChange={(event) => onChange({ minSpend: Number(event.currentTarget.value) || 0 })}
        />
        <TextInput
          label={`${fill(t.minPeriod, { d: ctx.periodDays })} · ${t.convAbbr}`}
          type="number"
          min={0}
          value={filters.minConversions === 0 ? '' : String(filters.minConversions)}
          onChange={(event) => onChange({ minConversions: Number(event.currentTarget.value) || 0 })}
        />

        {/* An input-height box, so the checkbox centres on the inputs' line rather than on
            the label-and-input blocks beside it (GRO-165). */}
        <div className="grv-filter-inline">
          <Checkbox
            label={t.showSamples}
            checked={filters.showSamples}
            onChange={(event) => onChange({ showSamples: event.currentTarget.checked })}
          />
        </div>

        {/* The table sorts from its headers; the grid has none, so the control lives here
            and drives the same state in both views. */}
        {view === 'grid' && (
          <Select
            label={t.sortBy}
            data={sortOptions.map(([value, label]) => ({ value, label }))}
            value={sort}
            onChange={(value) => value && onSort(value as SortKey)}
          />
        )}
      </div>
    </FilterBar>
  )
}
